import type React from 'react';
import type { PropsWithChildren } from 'react';
import { forwardRef, useCallback, useRef } from 'react';

import type { ButtonProps } from '../../Button';
import { Button } from '../../Button';
import { useForwardedRef } from '../../utils';
import {
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
} from './Components';
import { ModalContext } from './ModalContext';
import { ModalHeader } from './ModalHeader';
import type { ModalSize } from './ModalSize';

export interface ModalAction {
  disabled?: boolean;
  isLoading?: boolean;
  label: string;
  onClick?: () => void;
  semantic?: ButtonProps['semantic'];
}

type PaddingOptions = 'zero' | 'xxs' | 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl';

export interface ModalProps {
  bodyHorizontalPadding?: PaddingOptions;
  bodyVerticalPadding?: PaddingOptions;
  children?: React.ReactNode;
  disableScrollLock?: boolean;
  dismissible?: boolean;
  fullHeight?: boolean;
  testId?: string;
  onRequestClose?: () => void;
  position?: 'top' | 'center';
  primaryAction?: ModalAction;
  primaryActionButton?: React.ReactNode;
  secondaryAction?: ModalAction;
  shouldPreventClosingOnClickOutside?: boolean;
  size?: ModalSize | 'fullWidth';
  tertiaryAction?: ModalAction;
  title?: React.ReactNode;
  scrollable?: boolean;
  className?: string;
}

export const Modal = forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      bodyHorizontalPadding = 'm',
      bodyVerticalPadding = 'm',
      children,
      disableScrollLock = false,
      dismissible = true,
      fullHeight = false,
      onRequestClose = () => {},
      position = 'center',
      primaryAction,
      primaryActionButton,
      secondaryAction,
      shouldPreventClosingOnClickOutside = false,
      size = 'medium',
      tertiaryAction,
      title,
      testId,
      scrollable = true,
      className,
    },
    forwardedRef,
  ) => {
    const ref = useForwardedRef(forwardedRef);
    const handlePrimaryActionClick = useCallback(() => {
      if (primaryAction && !primaryAction.isLoading && primaryAction.onClick) {
        return primaryAction.onClick();
      }
    }, [primaryAction]);
    const tertiaryActionRef = useRef<HTMLButtonElement>(null);
    const secondaryActionRef = useRef<HTMLButtonElement>(null);
    const leastDestructiveRef = tertiaryAction
      ? tertiaryActionRef
      : secondaryActionRef;
    const handleClose = dismissible ? onRequestClose : () => {};

    return (
      <ModalOverlay
        onDismiss={shouldPreventClosingOnClickOutside ? () => {} : handleClose}
        initialFocusRef={leastDestructiveRef}
        allowPinchZoom
        dangerouslyBypassScrollLock={disableScrollLock}
        position={position}
        className={className}
      >
        <ModalContent
          ref={ref}
          data-testid={testId ?? 'modal-box'}
          size={size}
          fullHeight={fullHeight}
          aria-labelledby="modal-title"
        >
          <ModalContext.Provider
            value={{ dismissible, modalRef: ref.current, onClose: handleClose }}
          >
            <ModalHeader data-testid="header">{title}</ModalHeader>
            <ModalBody
              data-testid="body"
              fullHeight={fullHeight}
              className={`${horizontalPaddingMapping[bodyHorizontalPadding]} ${verticalPaddingMapping[bodyVerticalPadding]}`}
              scrollable={scrollable}
            >
              {children}
            </ModalBody>
            <ModalFooter data-testid="footer">
              {tertiaryAction ? (
                <Button
                  ref={tertiaryActionRef}
                  data-testid="tertiary-button"
                  semantic={tertiaryAction.semantic}
                  variant="tertiary"
                  onClick={tertiaryAction.onClick}
                  isLoading={tertiaryAction.isLoading}
                  disabled={tertiaryAction.disabled}
                >
                  {tertiaryAction.label}
                </Button>
              ) : (
                <div />
              )}
              <div className="mt-s gap-x-m gap-y-s tablet:mt-zero tablet:flex-row flex flex-col">
                {secondaryAction && (
                  <Button
                    ref={secondaryActionRef}
                    data-testid="secondary-button"
                    semantic={secondaryAction.semantic}
                    variant="secondary"
                    onClick={secondaryAction.onClick}
                    isLoading={secondaryAction.isLoading}
                    disabled={secondaryAction.disabled}
                  >
                    {secondaryAction.label}
                  </Button>
                )}
                {primaryAction && (
                  <Button
                    data-testid="primary-button"
                    onClick={handlePrimaryActionClick}
                    variant="primary"
                    semantic={primaryAction.semantic}
                    isLoading={primaryAction.isLoading}
                    disabled={primaryAction.disabled}
                  >
                    {primaryAction.label}
                  </Button>
                )}
                {!primaryAction && primaryActionButton}
              </div>
            </ModalFooter>
          </ModalContext.Provider>
        </ModalContent>
      </ModalOverlay>
    );
  },
);

export type SimpleModalProps = PropsWithChildren<{
  ariaLabel?: string;
  ariaLabelledBy?: string;
  dismissible?: boolean;
  fullHeight?: boolean;
  onRequestClose?: () => void;
  position?: 'top' | 'center';
  size?: ModalSize | 'fullWidth';
  testId?: string;
  allowOverlayScroll?: boolean;
  shouldPreventClosingOnClickOutside?: boolean;
}>;

const SimpleModalComponent = forwardRef<HTMLDivElement, SimpleModalProps>(
  (
    {
      ariaLabel,
      ariaLabelledBy,
      children,
      dismissible = true,
      fullHeight = false,
      onRequestClose,
      position = 'center',
      size = 'medium',
      allowOverlayScroll = false,
      shouldPreventClosingOnClickOutside = false,
      testId,
    },
    forwardedRef,
  ) => {
    const ref = useForwardedRef(forwardedRef);
    const handleClose = dismissible ? onRequestClose : () => {};
    return (
      <ModalOverlay
        allowPinchZoom
        onDismiss={shouldPreventClosingOnClickOutside ? () => {} : handleClose}
        position={position}
        className={allowOverlayScroll ? 'overflow-auto' : ''}
      >
        <ModalContent
          ref={ref}
          size={size}
          fullHeight={fullHeight}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          data-testid={testId ?? 'modal-box'}
        >
          <ModalContext.Provider
            value={{
              dismissible,
              modalRef: ref.current,
              onClose: onRequestClose,
            }}
          >
            {children}
          </ModalContext.Provider>
        </ModalContent>
      </ModalOverlay>
    );
  },
);

export const SimpleModal = Object.assign(SimpleModalComponent, {
  Header: ModalHeader,
  Body: ModalBody,
  Footer: ModalFooter,
});

const horizontalPaddingMapping: Record<PaddingOptions, string> = {
  zero: 'tablet:px-zero',
  xxs: 'tablet:px-xxs',
  xs: 'tablet:px-xs',
  s: 'tablet:px-s',
  m: 'tablet:px-m',
  l: 'tablet:px-l',
  xl: 'tablet:px-xl',
  xxl: 'tablet:px-xxl',
};

const verticalPaddingMapping: Record<PaddingOptions, string> = {
  zero: 'tablet:py-zero',
  xxs: 'tablet:py-xxs',
  xs: 'tablet:py-xs',
  s: 'tablet:py-s',
  m: 'tablet:py-m',
  l: 'tablet:py-l',
  xl: 'tablet:py-xl',
  xxl: 'tablet:py-xxl',
};
